import type { PolicyScenario, PolicyImpactResults } from '../services/policyImpactService';
import type { BuildingsCostResults } from '../services/buildingsCostModel';
import type { TransportCostResults } from '../services/transportCostModel';

export interface ScenarioReportInput {
  scenario: PolicyScenario;
  results: PolicyImpactResults;
  costResults: {
    buildings: BuildingsCostResults | null;
    transport: TransportCostResults | null;
  };
  breakdowns?: {
    stateReduction?: number;
    localReduction?: number;
    stateCost?: number;
    localCost?: number;
  };
}

export interface ScenarioReportOutput {
  executiveSummary: string;
  keyMetrics: {
    npv: number;
    costPerTon: number | null;
    paybackYear: number | null;
    totalReduction: number;
    upfrontCost: number;
  };
  stateVsLocal: {
    stateReduction: number;
    localReduction: number;
    stateCost: number;
    localCost: number;
  };
  sectorBreakdown: Array<{ sector: 'transport' | 'buildings' | 'waste'; reduction: number; cost: number }>;
  policyBreakdown: Array<{ policy: string; reduction: number }>;
  narrative: {
    summary: string;
    drivers: string;
    recommendations: string;
  };
  assumptions: {
    transportBAU: string;
    buildingsBAU: string;
    electricity: string;
    waste: string;
  };
}

const toPct = (value: number, total: number): string => {
  if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) return '0.0%';
  return `${((value / total) * 100).toFixed(1)}%`;
};

const money = (value: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

export function generateScenarioReport(input: ScenarioReportInput): ScenarioReportOutput {
  const { scenario, results, costResults, breakdowns } = input;
  const buildings = costResults.buildings;
  const transport = costResults.transport;

  const upfrontCost = (buildings?.costEffectivenessMetrics?.upfrontCapitalCost ?? 0) + (transport?.upfrontCapitalCost ?? 0);
  const npv = (buildings?.costEffectivenessMetrics?.netPresentValue ?? 0) + (transport?.netPresentValue ?? 0);
  const totalReduction = results.reductions.totalReduction ?? 0;

  const buildingsCostPerTon = buildings?.costEffectivenessMetrics?.costPerTonCO2eReduced ?? null;
  const transportCostPerTon = transport?.costPerTon ?? null;
  const weightedReduction =
    (buildings?.totalBuildingEmissionsReductionMT ?? 0) + (transport?.emissionsReductionMT ?? 0);
  const weightedCost =
    (buildings?.costEffectivenessMetrics?.cumulativeNetCost ?? 0) + (transport?.cumulativeNetCost ?? 0);
  const costPerTon = weightedReduction > 0 ? weightedCost / weightedReduction : (buildingsCostPerTon ?? transportCostPerTon ?? null);

  let paybackYear: number | null = null;
  const bSeries = buildings?.cumulativeNetCostSeries ?? [];
  const tSeries = transport?.cumulativeNetCostSeries ?? [];
  if (bSeries.length || tSeries.length) {
    const years = Array.from(new Set([...bSeries.map((r) => r.year), ...tSeries.map((r) => r.year)])).sort((a, b) => a - b);
    const bByYear = new Map(bSeries.map((r) => [r.year, r.value]));
    const tByYear = new Map(tSeries.map((r) => [r.year, r.value]));
    let seenPositive = false;
    for (const y of years) {
      const sum = (bByYear.get(y) ?? 0) + (tByYear.get(y) ?? 0);
      if (sum > 0) seenPositive = true;
      if (seenPositive && sum <= 0) {
        paybackYear = y;
        break;
      }
    }
  }

  const localReduction =
    breakdowns?.localReduction ??
    ((results.contributionsByPolicy.evAdoption ?? 0) +
      (results.contributionsByPolicy.vmtReduction ?? 0) +
      (results.contributionsByPolicy.heatPumpAdoption ?? 0) +
      (results.contributionsByPolicy.energyEfficiency ?? 0) +
      (results.contributionsByPolicy.organicsDiversion ?? 0));
  const stateReduction = breakdowns?.stateReduction ?? Math.max(0, totalReduction - localReduction);

  const localCost = breakdowns?.localCost ?? npv;
  const stateCost = breakdowns?.stateCost ?? 0;

  const sectorBreakdown: ScenarioReportOutput['sectorBreakdown'] = [
    {
      sector: 'transport',
      reduction: (results.reductions.lightDutyReduction ?? 0) + (results.reductions.heavyDutyReduction ?? 0),
      cost: transport?.netPresentValue ?? 0,
    },
    {
      sector: 'buildings',
      reduction: (results.reductions.naturalGasReduction ?? 0) + (results.reductions.electricityReduction ?? 0),
      cost: buildings?.costEffectivenessMetrics?.netPresentValue ?? 0,
    },
    {
      sector: 'waste',
      reduction: results.reductions.wasteReduction ?? 0,
      cost: 0,
    },
  ];

  const policyBreakdown: ScenarioReportOutput['policyBreakdown'] = [
    { policy: 'EV adoption', reduction: results.contributionsByPolicy.evAdoption ?? 0 },
    { policy: 'VMT reduction', reduction: results.contributionsByPolicy.vmtReduction ?? 0 },
    { policy: 'building electrification', reduction: results.contributionsByPolicy.heatPumpAdoption ?? 0 },
    { policy: 'energy efficiency', reduction: results.contributionsByPolicy.energyEfficiency ?? 0 },
    { policy: 'organics diversion', reduction: results.contributionsByPolicy.organicsDiversion ?? 0 },
  ].sort((a, b) => b.reduction - a.reduction);

  const topSector = sectorBreakdown.slice().sort((a, b) => b.reduction - a.reduction)[0];
  const executiveSummary = `Scenario reduces emissions by ${Math.round(totalReduction).toLocaleString()} tCO2e. Net present value is ${money(npv)} with ${paybackYear ? `payback by ${paybackYear}` : 'no modeled payback through 2045'}.`;

  const narrativeSummary = `Local policies contribute ${Math.round(localReduction).toLocaleString()} tCO2e (${toPct(localReduction, totalReduction)}), while state/background effects contribute ${Math.round(stateReduction).toLocaleString()} tCO2e (${toPct(stateReduction, totalReduction)}).`;
  const narrativeDrivers = `${topSector?.sector ?? 'transport'} is the largest sector driver in this run. Policy ranking is led by ${policyBreakdown.slice(0, 3).map((p) => p.policy).join(', ')}.`;
  const narrativeRecommendations =
    'Prioritize highest-reduction policies first, align financing to smooth upfront capital, and revisit rate assumptions regularly to improve electrification economics and payback outcomes.';

  const assumptions = {
    transportBAU: `EV adoption target ${Math.round(scenario.evAdoptionPercent)}%, VMT reduction ${Math.round(scenario.vmtReductionPercent * 10) / 10}%`,
    buildingsBAU: `Heat pump adoption ${Math.round(scenario.heatPumpAdoptionPercent)}%, efficiency target ${Math.round((scenario.energyEfficiencyTargetPercent ?? scenario.energyEfficiencyPercent ?? 0) * 10) / 10}%`,
    electricity: `Grid clean electricity year ${Math.round((scenario as { gridCleanElectricityYear?: number }).gridCleanElectricityYear ?? 2045)}`,
    waste: `Organics diversion ${Math.round(scenario.organicsDiversionPercent)}%`,
  };

  return {
    executiveSummary,
    keyMetrics: {
      npv,
      costPerTon,
      paybackYear,
      totalReduction,
      upfrontCost,
    },
    stateVsLocal: {
      stateReduction,
      localReduction,
      stateCost,
      localCost,
    },
    sectorBreakdown,
    policyBreakdown,
    narrative: {
      summary: narrativeSummary,
      drivers: narrativeDrivers,
      recommendations: narrativeRecommendations,
    },
    assumptions,
  };
}

